import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../app.js";
import { Api } from "../api.js";

import {
    Keyword,
    keywordHasAllApps,
    keywordNeedsPopularityUpdate,
    NaturalKeywordId,
    parseNaturalKeywordIdString,
    computeExactMatches,
} from "../model/keyword.js";
import { Country, countryToFlagEmoji } from "../model/country.js";
import "../components/header-component.js";
import "../components/rating-component.js";
import "../components/keyword-table-component.js";
import "../components/searchbar-component.js";
import "../components/dashboard-drawer-component.js";
import "../components/apps-list-component.js";
import "../components/dashboard-navbar-component.js";
import "../components/app-ranks-chart-component.js";

import { Store } from "../utils/store.js";
import { Platform } from "../model/platform.js";
import { App } from "../model/app.js";

@customElement("keyword-page")
export class KeywordPage extends BaseElement {
    @state() naturalKeywordId: NaturalKeywordId | null = null;
    @state() keyword: Keyword | null = null;

    @state() apps: App[] = [];
    @state() loading = false;

    @state() app: App | null = null;
    @state() appId: string | null = null;
    @state() appCountry: Country | null = null;
    @state() refreshing = false;

    connectedCallback() {
        super.connectedCallback();
        const path = window.location.pathname;
        const naturalKeywordIdString = path.split("/").pop() ?? "";

        this.naturalKeywordId = parseNaturalKeywordIdString(naturalKeywordIdString);
        const searchParams = new URLSearchParams(window.location.search);
        this.appId = searchParams.get("appId");
        this.appCountry = searchParams.get("appCountry") as Country | null;

        if (!this.naturalKeywordId) {
            window.location.href = "/search";
        } else {
            this.loadKeyword(false);
        }
    }

    async loadKeyword(allRankedApps: boolean) {
        const nkid = this.naturalKeywordId;
        if (!nkid) {
            alert("Missing keyword ID. Please try again.");
            return;
        }
        this.loading = true;
        const keyword = await Api.keyword(nkid.term, nkid.country, nkid.platform, allRankedApps, false);
        if (keyword instanceof Error) {
            alert("Error loading keyword. Please try again.");
        } else {
            if (this.appId && this.appCountry) {
                const connectedApp = await Api.getApp(this.appId, this.appCountry, keyword.id);
                if (connectedApp instanceof Error) {
                    alert("Error loading app. Please try again.");
                } else {
                    this.app = connectedApp;
                }
            }

            this.keyword = keyword;
            const apps: App[] = [];
            for (const rank of this.keyword.ranks ?? []) {
                if (rank.app !== undefined && rank.app.localization !== undefined) {
                    apps.push(rank.app);
                } else {
                    break;
                }
            }
            this.apps = apps;
        }
        this.loading = false;
    }

    async refreshKeyword() {
        const nkid = this.naturalKeywordId;
        if (!nkid) return;
        this.refreshing = true;
        const result = await Api.refreshKeyword(nkid.term, nkid.country, nkid.platform);
        if (result instanceof Error) {
            alert("Error refreshing keyword data. Please try again.");
        } else {
            this.keyword = result;
        }
        this.refreshing = false;
    }

    private formatTimeAgo(date: Date): string {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 1) return "today";
        if (diffDays === 1) return "1 day ago";
        if (diffDays < 30) return `${diffDays} days ago`;
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths === 1) return "1 month ago";
        return `${diffMonths} months ago`;
    }

    render() {
        return html`
            <div class="drawer lg:drawer-open">
                <input id="my-drawer" type="checkbox" class="drawer-toggle" />

                <div class="drawer-content">
                    <dashboard-navbar-component></dashboard-navbar-component>

                    ${this.keyword
                        ? html`
                              <div class="container p-4 md:p-6 lg:p-8 space-y-8">
                                  <div class="flex flex-row items-center justify-between space-x-4">
                                      <div class="flex flex-col space-x-4">
                                          <p class="text-lg text-gray-500">Keyword</p>
                                          <p class="text-4xl font-semibold">${this.keyword.term}</p>
                                      </div>

                                      <div class="flex items-center space-x-2">
                                          <p class="text-lg">${countryToFlagEmoji(this.keyword.country)}</p>
                                          <p class="text-lg text-gray-500">
                                              ${Object.keys(Platform).find(
                                                  (key) => Platform[key as keyof typeof Platform] === this.keyword?.platform
                                              )}
                                          </p>
                                      </div>
                                  </div>

                                  <div>
                                      <div class="flex flex-row items-center space-x-8">
                                          <div>
                                              <p class="text-lg text-gray-500">Popularity</p>
                                              <p class="text-5xl text-primary font-bold">${this.keyword.popularity}</p>
                                          </div>
                                          <div>
                                              <p class="text-lg text-gray-500">Difficulty</p>
                                              <p class="text-5xl text-primary font-bold">${this.keyword.difficulty}</p>
                                          </div>
                                          <div>
                                              <p class="text-lg text-gray-500">Results</p>
                                              <p class="text-5xl text-primary font-bold">${(this.keyword.ranks ?? []).length}</p>
                                          </div>
                                      </div>
                                      <div class="flex items-center gap-2 mt-2">
                                          ${this.keyword.popularityUpdated
                                              ? html`<p class="text-xs text-gray-400">
                                                    Popularity last updated ${this.formatTimeAgo(new Date(this.keyword.popularityUpdated))}
                                                </p>`
                                              : html`<p class="text-xs text-gray-400">Popularity never updated</p>`}
                                          ${keywordNeedsPopularityUpdate(this.keyword)
                                              ? html`<button
                                                    class="btn btn-xs bg-base-200 border-none text-base-content"
                                                    @click=${() => this.refreshKeyword()}
                                                    ?disabled=${this.refreshing}
                                                >
                                                    ${this.refreshing
                                                        ? html`<span class="loading loading-spinner loading-xs"></span> Refreshing…`
                                                        : html`↻ Refresh`}
                                                </button>`
                                              : ""}
                                      </div>
                                  </div>

                                  ${this.app
                                      ? html`
                                            <div class="flex flex-col">
                                                <div class="flex flex-row items-center justify-between">
                                                    <h3>Rank History</h3>
                                                    <div class="flex flex-row items-center gap-2 rounded-lg p-2">
                                                        <img src=${this.app.icon} alt=${this.app.localization?.title} class="w-8 h-8 rounded-lg" />
                                                        <div class="flex flex-col">
                                                            <div class="text-sm font-semibold">${this.app.localization?.title}</div>
                                                            <div class="text-sm text-gray-500">${this.app.localization?.subtitle ?? "--"}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <app-ranks-chart-component .app=${this.app}></app-ranks-chart-component>
                                            </div>
                                        `
                                      : ""}
                                  <div>
                                      <h3 class="mb-1">Ranks</h3>
                                      <p class="text-sm text-gray-500">
                                          ${computeExactMatches(this.keyword)}/10 Exact <span class="bg-yellow-100 px-1 rounded">Matches</span> in
                                          Title or Subtitle
                                      </p>
                                  </div>
                                  <div class="flex flex-col">
                                      <apps-list-component .term=${this.keyword?.term} .apps=${this.apps}></apps-list-component>

                                      ${!keywordHasAllApps(this.keyword)
                                          ? html`
                                                <button class="btn mx-auto pt-4" @click=${() => this.loadKeyword(true)} ?disabled=${this.loading}>
                                                    ${this.loading ? html`Loading...` : html`Load All Apps`}
                                                </button>
                                            `
                                          : ""}
                                  </div>
                              </div>
                          `
                        : html``}
                </div>
                <div class="drawer-side">
                    <label for="my-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                    <dashboard-drawer-component></dashboard-drawer-component>
                </div>
            </div>
        `;
    }
}
